import React, { useEffect, useState } from "react";
import { TextField, InputAdornment, CircularProgress } from "@mui/material";
import { api, RouteAPI } from "../service/apiService";
import VerifiedIcon from "@mui/icons-material/Verified";
import CancelIcon from "@mui/icons-material/Cancel";
import { useDebounce } from "use-debounce";
import { useTranslation } from "react-i18next";
import { use } from "i18next";

interface SiretInputProps {
    value: string;
    onChange: (value: string) => void;
    onValidate?: (isValid: boolean) => void;
    label?: string;
    required?: boolean;
}

const SiretInput: React.FC<SiretInputProps> = ({
    value,
    onChange,
    onValidate,
    label = "SIRET",
    required = false,
}) => {
    const [isValid, setIsValid] = useState<boolean | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [siretMessageKey, setSiretMessageKey] = useState<string | null>(null);

    const [debouncedValue] = useDebounce(value, 500);
    const { t } = useTranslation();

    const requestSiret = async (siret: string) => {
        if (siret.length !== 14) {
            setIsValid(null);
            setSiretMessageKey(null);
            return;
        }

        setLoading(true);
        try {
            const { data, status } = await api(
                `${RouteAPI.SIRET_NUMBER}/${siret}`,
                "GET"
            );

            const isActive = status === 200 && data.valid && data.active;
            setIsValid(isActive);

            if (!isActive) {
                setSiretMessageKey(data.message_key || "siret.inactive");
            } else {
                setSiretMessageKey(null);
            }
        } catch (error: unknown) {
            setIsValid(false);
            setSiretMessageKey("siret.invalid");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (debouncedValue.length === 14) {
            requestSiret(debouncedValue);
        } else {
            setIsValid(null);
        }
    }, [debouncedValue]);

    useEffect(() => {
        onValidate?.(isValid ?? false);
    }, [isValid]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value);
        setSiretMessageKey(null);
        setIsValid(null);
    };

    return (
        <>
            <TextField
                fullWidth
                required={required}
                label={label}
                name="siret"
                value={value}
                onChange={handleChange}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            {loading ? (
                                <CircularProgress size={20} />
                            ) : isValid !== null ? (
                                isValid ? (
                                    <VerifiedIcon
                                        style={{ color: "#4BB543" }}
                                    />
                                ) : (
                                    <CancelIcon style={{ color: "#CC0000" }} />
                                )
                            ) : null}
                        </InputAdornment>
                    ),
                }}
            />
            {siretMessageKey && (
                <div
                    style={{
                        fontSize: "12px",
                        marginTop: "4px",
                        color: isValid ? "#4BB543" : "#CC0000",
                    }}
                >
                    {t(`error.${siretMessageKey}`)}
                </div>
            )}
        </>
    );
};

export default SiretInput;
